/** Company Expenses (v1 qbo-expenses.js companyExpView). The COMPANY_EXP
 * org-settings array: business (non-property) expenses. Add / edit / delete
 * write the WHOLE array through the settings door (PHASE19), gated by liveFire
 * `expensesEdit`. QuickBooks connect + two-way sync stay in v1 (OAuth) — shown
 * as a read-only note here. */
import { useEffect, useMemo, useState } from "react";
import { moneyExact } from "../../lib/money";
import { liveOn } from "../../lib/liveFire";
import { todayIsoAppTz } from "../../lib/apptime";
import { useConfirmDelete } from "../common/ConfirmDelete";
import {
  COMPANY_CATS, LEX_PAY, EXPENSE_MSG,
  allCompanyExpensesRaw, narrowCompanyExpense, companyExpenseToRaw, companyMonthYtd,
  buildExpenseCsv, expenseId, isoToDisplay,
  type CompanyExpense,
} from "./api";
import { fetchQboStatus, qboConnectUrl, qboDisconnect, qboPush, readQboReturn, type QboStatus } from "./qbo";
import { BackendError } from "../../lib/backend";

const money = moneyExact;

function downloadCsv(name: string, csv: string): void {
  try {
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch { /* non-DOM env (smoke) */ }
}

export function CompanyExpensesPage({
  orgSettings,
  onSave,
  token,
  settingsReady = true,
}: {
  orgSettings: Record<string, unknown>;
  onSave: (all: Record<string, unknown>[]) => void;
  token: string | null;
  /** org settings have actually been READ — see ExpensesPage. */
  settingsReady?: boolean;
}) {
  const canEdit = liveOn("expensesEdit");
  const canSync = liveOn("qboSync");
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [editing, setEditing] = useState<CompanyExpense | "new" | null>(null);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [syncNote, setSyncNote] = useState<string | null>(null);

  const list = useMemo(() => allCompanyExpensesRaw(orgSettings).map(narrowCompanyExpense), [orgSettings]);

  const now = new Date();
  const tiles = useMemo(() => companyMonthYtd(list, now.getFullYear(), now.getMonth()), [list, now]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return list.filter((e) => {
      if (cat && e.cat !== cat) return false;
      if (!needle) return true;
      return (e.desc + " " + e.vendor + " " + e.cat + " " + e.notes).toLowerCase().includes(needle);
    });
  }, [list, q, cat]);

  // Mutate the WHOLE array (v1 blob semantics), then persist through the door.
  const save = (mutate: (all: Record<string, unknown>[]) => void) => {
    const all = allCompanyExpensesRaw(orgSettings).map((r) => ({ ...r }));
    mutate(all);
    onSave(all);
  };
  const upsert = (e: CompanyExpense) => {
    save((all) => {
      const raw = companyExpenseToRaw(e);
      const i = all.findIndex((r) => String(r["id"]) === e.id);
      if (i >= 0) all[i] = raw; else all.unshift(raw);
    });
    setEditing(null);
  };
  // v1 ceDelete — typed-delete gated, exact wording.
  const confirmDel = useConfirmDelete();
  const remove = (id: string) => confirmDel.ask("Delete this company expense? (It is NOT deleted in QuickBooks.)", () => save((all) => {
    const i = all.findIndex((r) => String(r["id"]) === id);
    if (i >= 0) all.splice(i, 1);
  }));

  // Push one expense into QuickBooks (GATED — qboSync). On success, stamp the
  // returned QBO id + a sync timestamp back onto the record through the door.
  const syncOne = async (e: CompanyExpense) => {
    if (!canSync || syncing) return;
    setSyncing(e.id); setSyncNote(null);
    try {
      const r = await qboPush(token, {
        crm_id: e.id, date: e.dateIso, desc: e.desc, category: e.cat,
        amount: e.amt + e.tax, payment: e.pay, vendor: e.vendor, property: "",
        ...(e.qboId ? { qbo_id: e.qboId } : {}),
      });
      save((all) => {
        const i = all.findIndex((r2) => String(r2["id"]) === e.id);
        if (i >= 0) { all[i] = { ...all[i], qboId: r.qbo_id, qboSync: new Date().toISOString() }; }
      });
      setSyncNote(`Synced "${e.desc}" to QuickBooks ✓`);
    } catch (err) {
      setSyncNote(err instanceof BackendError ? "Sync failed: " + err.detail : "Sync failed");
    } finally {
      setSyncing(null);
    }
  };

  return (
    <div className="leads exppage">
      <div className="exphead">
        <div>
          <h1>Company Expenses</h1>
          <div className="scsub">Business costs not tied to a property — synced two-way with QuickBooks</div>
        </div>
        <div className="expacts">
          <button className="btn slim" onClick={() => downloadCsv("company-expenses.csv", buildExpenseCsv(
            ["Date", "Description", "Category", "Vendor", "Payment", "Amount", "Tax", "Status"],
            list.map((e) => [isoToDisplay(e.dateIso), e.desc, e.cat, e.vendor, e.pay, e.amt, e.tax, e.st]),
          ))}>⤓ Export</button>
          {canEdit ? <button className="btn slim primary" onClick={() => setEditing("new")}>+ Add expense</button> : null}
        </div>
      </div>

      <div className="dkpis exptiles">
        <div className="dkpi"><span className="lbl">This month</span><strong>{money(tiles.month)}</strong></div>
        <div className="dkpi"><span className="lbl">Year to date</span><strong>{money(tiles.ytd)}</strong></div>
        <div className="dkpi"><span className="lbl">Total records</span><strong>{list.length}</strong></div>
      </div>

      <QuickBooksPanel token={token} canSync={canSync} />
      {syncNote ? <div className="inbxnote">{syncNote}</div> : null}

      <div className="bar">
        <input placeholder="Search description, vendor, category…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select value={cat} onChange={(e) => setCat(e.target.value)}>
          <option value="">All categories</option>
          {COMPANY_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="count">{shown.length} of {list.length}</span>
      </div>

      {editing ? (
        <ExpenseForm
          initial={editing === "new" ? null : editing}
          onCancel={() => setEditing(null)}
          onSubmit={upsert}
        />
      ) : null}

      <div className="list explist">
        <table className="exptable">
          <thead>
            <tr><th>Date</th><th>Description</th><th>Category</th><th>Vendor</th><th>Payment</th><th className="r">Amount</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {shown.length ? shown.map((e) => (
              <tr key={e.id || e.desc + e.dateIso}>
                <td className="dim">{isoToDisplay(e.dateIso) || "—"}</td>
                <td>{e.desc || "—"}{e.qboId ? <span className="qbchip" title="From QuickBooks">QB</span> : null}</td>
                <td><span className="expcat">{e.cat}</span></td>
                <td>{e.vendor || "—"}</td>
                <td className="dim">{e.pay || "—"}</td>
                <td className="r"><strong>{money(e.amt + e.tax)}</strong></td>
                <td><span className="statuschip">{e.st}</span></td>
                <td className="r">
                  {canSync ? (
                    <button className="rowbtn" title={e.qboId ? "Re-sync to QuickBooks" : "Sync to QuickBooks"} disabled={syncing === e.id} onClick={() => syncOne(e)}>
                      {syncing === e.id ? "…" : e.qboId ? "⟳" : "⇪"}
                    </button>
                  ) : null}
                  {canEdit ? (
                    <>
                      <button className="rowbtn" title="Edit" onClick={() => setEditing(e)}>✎</button>
                      <button className="rowbtn del" title="Delete" onClick={() => remove(e.id)}>✕</button>
                    </>
                  ) : null}
                </td>
              </tr>
            )) : (
              <tr><td colSpan={8} className="expempty">{
                !settingsReady
                  ? "Loading your expenses… this is not an empty ledger — don't add anything until it appears."
                  : canEdit ? "No company expenses yet — add one." : EXPENSE_MSG.none
              }</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {confirmDel.modal}
    </div>
  );
}

/** QuickBooks connection panel (v1 qbo-expenses.js). Status + connect (OAuth
 * redirect) / disconnect. Live-fires nothing itself; the per-row Sync button is
 * separately gated by qboSync. */
function QuickBooksPanel({ token, canSync }: { token: string | null; canSync: boolean }) {
  const [st, setSt] = useState<QboStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = () => {
    setLoading(true); setErr(null);
    void fetchQboStatus(token)
      .then(setSt)
      .catch((e) => setErr(e instanceof BackendError ? e.detail : "Could not reach the backend"))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    // If we just came back from the Intuit OAuth round-trip, clean the URL.
    const r = readQboReturn();
    if (r) { try { window.history.replaceState({}, "", window.location.pathname); } catch { /* noop */ } }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const disconnect = async () => {
    // "surfaced on reload" was not true: load() sat INSIDE the try, after the
    // await, so a failed disconnect skipped it entirely — no refresh, no error,
    // and the panel kept saying "Connected". You'd think QuickBooks was
    // unlinked while it was still linked. Report the failure, and reload either
    // way so the panel reflects the server rather than an assumption.
    try {
      await qboDisconnect(token);
      setErr(null);
    } catch (e) {
      setErr(e instanceof BackendError ? e.detail : "Disconnect failed — QuickBooks is still connected.");
    } finally {
      load();
    }
  };

  return (
    <div className="qbopanel">
      <div className="qbomain">
        <span className="qboico">📗</span>
        <div>
          <strong>QuickBooks Online</strong>
          <div className="sub">
            {loading ? "Checking connection…"
              : err ? err
              : !st?.configured ? "Not set up on the backend yet (no QuickBooks app credentials)."
              : st.connected ? `Connected${st.company ? " — " + st.company : ""}.${canSync ? " Use the sync button on a row to push it." : " Syncing is off on this beta."}`
              : "Not connected — link your QuickBooks company to sync expenses."}
          </div>
        </div>
      </div>
      <div className="qboacts">
        {!loading && !err && st?.configured && !st.connected ? (
          <a className="btn slim primary" href={qboConnectUrl()}>Connect QuickBooks</a>
        ) : null}
        {!loading && st?.connected ? <button className="btn slim" onClick={disconnect}>Disconnect</button> : null}
        <button className="btn slim" onClick={load} disabled={loading}>Refresh</button>
      </div>
    </div>
  );
}

function ExpenseForm({ initial, onCancel, onSubmit }: { initial: CompanyExpense | null; onCancel: () => void; onSubmit: (e: CompanyExpense) => void }) {
  // Eastern, not UTC — toISOString() rolls the date over at 8pm ET, which put
  // evening expenses in the wrong month, and into QuickBooks that way.
  const [dateIso, setDateIso] = useState(initial?.dateIso ?? todayIsoAppTz());
  const [desc, setDesc] = useState(initial?.desc ?? "");
  const [cat, setCat] = useState(initial?.cat ?? "Other");
  const [amt, setAmt] = useState(initial ? String(initial.amt) : "");
  const [tax, setTax] = useState(initial ? String(initial.tax) : "");
  const [pay, setPay] = useState(initial?.pay ?? "None / N/A");
  const [vendor, setVendor] = useState(initial?.vendor ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [receipt, setReceipt] = useState("");
  const a = Number(amt.replace(/[^0-9.]/g, ""));
  const tx = Number(tax.replace(/[^0-9.]/g, ""));
  const sub = Number.isFinite(a) ? a : 0;
  const taxN = Number.isFinite(tx) ? tx : 0;
  const money2 = (n: number): string => "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const ok = desc.trim() !== "" && Number.isFinite(a) && a >= 0;
  // Not built — see the identical note in ExpensesPage. The file was never
  // uploaded and `receipt` is not in the submit payload, so "— attached" was a
  // claim about a file that had already been discarded.
  const pickReceipt = () => { /* intentionally inert */ };
  const submit = () => ok && onSubmit({
    id: initial?.id || expenseId(Date.now()),
    dateIso, desc: desc.trim(), cat, amt: sub, tax: taxN,
    pay, st: initial?.st || "Paid", vendor: vendor.trim(), notes: notes.trim(),
    qboId: initial?.qboId ?? "", qboSync: initial?.qboSync ?? "",
  });
  return (
    <div className="expform">
      <div className="expformtitle">{initial ? "Edit expense" : "💼 Add company expense"}</div>
      <button type="button" className="expreceipt" onClick={pickReceipt} disabled
              title="Receipt storage isn't built yet — keep the file with your records">
        <span className="expreceipticon" aria-hidden="true">⬆</span>
        <span className="expreceiptmain">Receipt upload — not available yet</span>
        <span className="expreceiptsub">PNG, JPG, HEIC or PDF{receipt ? "" : " — keep a copy on the expense"}</span>
      </button>
      <div className="expformgrid">
        <label className="wide">Description<input value={desc} placeholder="e.g. Software subscription" onChange={(e) => setDesc(e.target.value)} /></label>
        <label>Date<input type="date" value={dateIso} onChange={(e) => setDateIso(e.target.value)} /></label>
        <label>Category<select value={cat} onChange={(e) => setCat(e.target.value)}>{COMPANY_CATS.map((c) => <option key={c}>{c}</option>)}</select></label>
        <label>Amount ($)<input inputMode="decimal" value={amt} placeholder="0.00" onChange={(e) => setAmt(e.target.value)} /></label>
        <label>Tax ($)<input inputMode="decimal" value={tax} placeholder="0" onChange={(e) => setTax(e.target.value)} /></label>
        <label>Payment<select value={pay} onChange={(e) => setPay(e.target.value)}>{LEX_PAY.map((p) => <option key={p}>{p}</option>)}{initial && initial.pay && !LEX_PAY.includes(initial.pay) ? <option value={initial.pay}>{initial.pay}</option> : null}</select></label>
        <label>Vendor<input value={vendor} placeholder="Who was paid?" onChange={(e) => setVendor(e.target.value)} /></label>
        <label className="wide">Notes<input value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
      </div>
      <div className="exprollup">
        <div><span>Subtotal</span><b>{money2(sub)}</b></div>
        <div><span>Tax</span><b>{money2(taxN)}</b></div>
        <div className="grand"><span>Grand Total</span><b>{money2(sub + taxN)}</b></div>
      </div>
      <div className="expformacts">
        <button className="btn slim primary" disabled={!ok} onClick={submit}>{initial ? "Save" : "Add expense"}</button>
        <button className="btn slim" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
